import type { ReactNode } from "react";

export type StatePanelKind =
	| "loading"
	| "empty"
	| "blocked"
	| "error"
	| "readonly"
	| "completed";

export type MobileEmptyStateReason =
	| "empty"
	| "filtered"
	| "not-started"
	| "unavailable";

type StatePanelProps = {
	action?: ReactNode;
	children: ReactNode;
	emptyReason?: MobileEmptyStateReason;
	kind: StatePanelKind;
	title: ReactNode;
};

export const StatePanel = ({
	action,
	children,
	emptyReason,
	kind,
	title,
}: StatePanelProps) => {
	const resolvedEmptyReason =
		kind === "empty" ? (emptyReason ?? "empty") : undefined;

	return (
		<div
			className={`state-panel state-panel-${kind} alert alert-light border text-body my-3`}
			data-mobile-empty-state={kind === "empty" ? "" : undefined}
			data-mobile-empty-state-action={
				kind === "empty" ? (action ? "available" : "none") : undefined
			}
			data-mobile-empty-state-reason={resolvedEmptyReason}
			data-mobile-state-kind={kind}
			data-mobile-state-panel=""
			role={kind === "error" ? "alert" : "status"}
		>
			<h2 className="h4 mb-2">{title}</h2>
			<p className="mb-0">{children}</p>
			{action ? <div className="state-panel-actions mt-3">{action}</div> : null}
		</div>
	);
};

type SpecificStatePanelProps = Omit<StatePanelProps, "kind">;

export const LoadingState = (props: SpecificStatePanelProps) => (
	<StatePanel kind="loading" {...props} />
);

export const EmptyStatePanel = (props: SpecificStatePanelProps) => (
	<StatePanel kind="empty" {...props} />
);

export const BlockedState = (props: SpecificStatePanelProps) => (
	<StatePanel kind="blocked" {...props} />
);

export const ErrorState = (props: SpecificStatePanelProps) => (
	<StatePanel kind="error" {...props} />
);

export const ReadOnlyState = (props: SpecificStatePanelProps) => (
	<StatePanel kind="readonly" {...props} />
);

export const CompletedState = (props: SpecificStatePanelProps) => (
	<StatePanel kind="completed" {...props} />
);

export default StatePanel;
